import moment from "moment-timezone";
import userTypeConsts from "../../../../../constants/userTypeConsts.js";

/**
 * Get time components from a time string
 * @param {string} timeString - Time string in format "HH:mm:ss"
 * @returns {{hours: number, minutes: number, seconds: number}}
 */
export function getTimeComponents(timeString) {
  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  return { hours, minutes, seconds };
}

/**
 * Check if hours are locked based on date and timer lock settings
 * @param {Date|string} date - Date to check
 * @param {string} timeZone - Timezone string
 * @param {string} format - Date format string
 * @param {Object} user - User object to check user type
 * @returns {boolean} - True if unlocked, false if locked
 */
export function getLockHours(date, timeZone, format, user) {
  if (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) {
    return true;
  }
  
  try {
    const lockData = global.config.timerLock();
    
    if (!lockData) {
      return true; // Default to unlocked if no lock data
    }
    
    const tz = global.config.getTimeZone();
    const currentDate = moment().tz(tz).startOf('day'); // Normalize to start of day for consistent comparison
    const days = parseInt(lockData.timerLock, 10); // Convert to number
    const time = lockData.timerLockTime;
    
    if (!days || !time) {
      return true; // Default to unlocked if missing data
    }
    
    const time24Hour = moment(time, "h:mm A").format("HH:mm:ss");
    const timeComponents = getTimeComponents(time24Hour);
    
    // Calculate lock date: current date minus (days + 1), at start of that day
    // The lock date represents the cutoff - dates on or before this date should be disabled
    // We subtract (days + 1) so that (currentDate - days) becomes the first enabled date
    const lockDate = moment(currentDate).subtract(days + 1, "days").startOf('day');
    
    // Input date from DatePicker is at midnight, so parse it in the timezone and set to start of day
    // Parse the date string directly in the target timezone to avoid timezone conversion issues
    const inputDate = moment.tz(date, format || "YYYY-MM-DD HH:mm:ss", tz).startOf('day');
    
    // Return true if input date is AFTER lock date (unlocked), false if <= lock date (locked)
    // Dates on or before the lock date should be disabled (locked)
    // Use isAfter() which returns false when dates are equal, ensuring lock date itself is disabled
    return inputDate.isAfter(lockDate);
  } catch (error) {
    console.error("Error in getLockHours:", error);
    return true; // Default to unlocked on error
  }
}

/**
 * Check if any time input has focus
 * @param {Object} refs - Object containing refs for time inputs
 * @param {Object} refs.startInputRef - Ref for start time input (24hr)
 * @param {Object} refs.endInputRef - Ref for end time input (24hr)
 * @param {Object} refs.start12HrTimeInputRef - Ref for start time input (12hr)
 * @param {Object} refs.end12HrTimeInputRef - Ref for end time input (12hr)
 * @returns {boolean} - True if any time input has focus
 */
export function isAnyTimeInputFocused({ startInputRef, endInputRef, start12HrTimeInputRef, end12HrTimeInputRef }) {
  const activeElement = document.activeElement;
  return activeElement === startInputRef?.current ||
    activeElement === endInputRef?.current ||
    activeElement === start12HrTimeInputRef?.current ||
    activeElement === end12HrTimeInputRef?.current;
}

/**
 * Handle date picker icon click to open date picker
 * @param {Object} datePickerRef - Ref to the date picker component
 */
export function handleDateIconClick(datePickerRef) {
  try {
    if (datePickerRef?.current && typeof datePickerRef.current.open === "function") {
      datePickerRef.current.open(); // Open the date picker programmatically
    } else {
      console.warn("Date picker ref is not available or does not have an open method.");
    }
  } catch (error) {
    console.error("Error while opening the date picker:", error);
  }
}

/**
 * Handle pagination page change
 * @param {Object} params - Parameters object
 * @param {Object} params.selected - Selected page object with selected property
 * @param {boolean} params.isTimerLoading - Whether timer data is loading
 * @param {Function} params.setSelectedPage - Setter for selected page
 * @param {Function} params.setTimerPage - Setter for timer page (1-based)
 */
export function handlePageChange({ selected, isTimerLoading, setSelectedPage, setTimerPage }) {
  try {
    // Prevent multiple rapid page changes while loading
    if (isTimerLoading) {
      return;
    }

    if (!selected || typeof selected.selected !== "number") {
      console.error("handlePageChange: Invalid selected object:", selected);
      return;
    }

    // Update selectedPage immediately for instant visual feedback
    setSelectedPage(selected.selected);

    // Convert from 0-based index to 1-based page number for server
    // This will trigger data fetch, and loading state will be shown
    setTimerPage(selected.selected + 1);
  } catch (error) {
    console.error("handlePageChange: Unexpected error:", error);
  }
}

/**
 * Check if user is admin or super admin
 * @param {Object} user - User object
 * @returns {boolean} - True if user is admin or super admin
 */
export function isAdminUser(user) {
  return user?.userType === userTypeConsts.SUPER_ADMIN || user?.userType === userTypeConsts.ADMIN;
}
